import { useEffect, useRef, useState } from "react";

// Live in-app camera preview + capture, built from scratch - confirmed
// nothing like this exists anywhere in the app today (every other "camera"
// entry point is a plain <input capture="environment"> delegating to the
// OS camera app). Rear camera by default (facingMode "environment"),
// mirrors nothing (a rear-camera feed shouldn't be mirrored).
export function CameraCaptureView({
  onCapture,
  onCancel,
  onFallbackToGallery,
}: {
  onCapture: (file: File) => void;
  onCancel: () => void;
  onFallbackToGallery: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (err) {
        // Distinct messages per failure reason (NotAllowedError/
        // NotFoundError/NotReadableError etc.) - the voice recorder's own
        // blanket "Microphone access denied." catch was confirmed too
        // coarse to reuse verbatim for camera, since "no camera" and
        // "camera busy" need different guidance than a plain denial.
        const name = err instanceof Error ? err.name : "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          setError("Camera access is needed to take a photo here.");
        } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          setError("No camera found on this device.");
        } else if (name === "NotReadableError" || name === "TrackStartError") {
          setError("Your camera is already in use by another app.");
        } else {
          setError("Couldn't access the camera.");
        }
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  function handleCapture(): void {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onCapture(new File([blob], `photo-${Date.now()}.jpg`, { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.92,
    );
  }

  return (
    <div className="private-drop-capture">
      <div className="private-drop-capture-stage">
        {error ? (
          <div className="private-drop-capture-error">
            <i className="bi bi-camera-video-off" aria-hidden="true" />
            <p>{error}</p>
            <button type="button" className="btn btn-pink btn-sm" onClick={onFallbackToGallery}>
              Choose from Gallery
            </button>
          </div>
        ) : (
          <video ref={videoRef} playsInline muted className="private-drop-capture-video" />
        )}
      </div>
      <div className="private-drop-capture-controls">
        <button type="button" className="private-drop-capture-cancel" aria-label="Cancel" onClick={onCancel}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>
        {!error && (
          <button type="button" className="private-drop-capture-shutter" aria-label="Take photo" onClick={handleCapture} disabled={!ready} />
        )}
      </div>
    </div>
  );
}
